import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import fileDownload from 'js-file-download';
import { Button, Table, Tag } from 'antd';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useApiClient from '../../../hooks/api/useApiClient';
import { TeamReportFormat, TeamReportType } from '../../../services/exports/Constants';
import useHelpers from '../../../hooks/context/useHelpers';
import DateRangePicker, { filterFactory, filter_this_month, variant_dropdown } from '../../filters/DateRangePicker';
import ManagerSelect from '../../form/business/ManagerSelect';
import usePermissions, { ManagerPermission, ManagerRole } from '../../../hooks/auth/usePermissions';
import useDateRangeFilter from '../../../hooks/utility/useDateRangeFilter';

export default function SalesReport() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Team:Reports:SalesReport' });
  const { t: tRoles } = useTranslation(undefined, { keyPrefix: 'Shared:Roles' });

  const { TeamReportsManager } = useApiClient();

  const { profile } = useSelector((state) => state.initialData);

  const { formatDate, formatDateTime } = useHelpers();
  const { can } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const hasManagerSelect = can(ManagerPermission.FullAccessGeneralReports);
  const [selectedManagers, setSelectedManagers] = useState(hasManagerSelect ? [] : [profile.id]);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter(filterFactory(filter_this_month));

  useEffect(() => {
    selectedManagers?.length > 0 && loadReport(TeamReportFormat.JSON);
  }, [selectedManagers, dateRangeFilter]);

  const onLoadManagers = ({ data, setManagers }) => {
    const filteredManagers = collect(data)
      .filter((item) =>
        collect(item.roles)
          .intersect([
            ManagerRole.SuperAdmin,
            ManagerRole.Admin,
            ManagerRole.Reseller,
            ManagerRole.SalesManager,
            ManagerRole.OnboardingManager,
            ManagerRole.SalesLeadership,
          ])
          .isNotEmpty(),
      )
      .toArray();

    setManagers(filteredManagers);
    setSelectedManagers(
      collect(filteredManagers)
        .filter((item) =>
          collect(item.roles)
            .intersect([ManagerRole.Reseller, ManagerRole.SalesManager, ManagerRole.SalesLeadership])
            .isNotEmpty(),
        )
        .pluck('id')
        .toArray(),
    );
    setLoading(false);
  };

  const loadReport = async (format) => {
    setLoading(true);
    const { success, data } = await TeamReportsManager.create({
      format,
      type: TeamReportType.Sales,
      start: dateRangeFilter?.start_date,
      end: dateRangeFilter?.end_date,
      managers: selectedManagers,
    });
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return null;
    }

    format === TeamReportFormat.JSON && setReport(data);

    return data;
  };

  const columns = [
    {
      title: t('fields.name'),
      key: 'name',
      render: (_, item) => (item.manager.name ? item.manager.name : item.manager.email),
    },
    {
      title: t('fields.area'),
      key: 'location',
      dataIndex: ['manager', 'location'],
    },
    {
      title: t('fields.signed'),
      key: 'signed',
      render: (_, item) => <Tag color="default">{item.places_signed.length}</Tag>,
    },
    {
      title: t('fields.live'),
      key: 'live',
      render: (_, item) => <Tag color="success">{item.places_enabled.length}</Tag>,
    },
  ];

  const renderDetails = (item) => (
    <Table
      columns={[
        { title: t('fields.name'), key: 'company_name', dataIndex: 'name' },
        { title: t('fields.area'), key: 'company_city', dataIndex: 'city' },
        {
          title: t('fields.signed'),
          key: 'signed_at',
          dataIndex: 'signed_at',
          render: (_, item) => formatDateTime(item.signed_at),
        },
        {
          title: t('fields.live'),
          key: 'live_from',
          dataIndex: 'live_from',
          render: (_, item) => (item.live_from ? formatDateTime(item.live_from) : '-'),
        },
      ]}
      dataSource={collect(item.places_enabled).merge(item.places_signed).unique('id').toArray()}
      rowKey={(row) => `company-${row.id}`}
      pagination={false}
    />
  );

  return (
    <>
      <div className="flex justify-between">
        <h2>{t('title')}</h2>
        {report && (
          <Button
            type="primary"
            size="large"
            icon={<ExportOutlined />}
            loading={loading}
            onClick={() => {
              const startDate = formatDate(dateRangeFilter.start_date);
              const endDate = formatDate(dateRangeFilter.end_date);

              const filename = `sales-team-report-${startDate}-${endDate}.csv`;
              loadReport(TeamReportFormat.File).then((data) => fileDownload(data, filename));
            }}
          >
            {t('buttons.download_report')}
          </Button>
        )}
      </div>
      <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
        <div className="flex lg:flex-row flex-col justify-between items-center gap-4 p-md border-b border-brand-inkGrey-grey_2">
          <div>
            <DateRangePicker
              variant={variant_dropdown}
              defaultFilter={filter_this_month}
              initialValue={dateRangeFilter}
              onChange={setDateRangeFilter}
            />
            {hasManagerSelect && (
              <ManagerSelect
                value={selectedManagers}
                isMulti
                onLoad={onLoadManagers}
                formatOptions={(managers) =>
                  collect(managers)
                    .groupBy((item) => item.roles[0])
                    .map((managers, role) => ({
                      label: tRoles(role),
                      title: tRoles(role),
                      options: managers.map((item) => ({ label: item.name, value: item.id })).toArray(),
                    }))
                    .toArray()
                }
                onChange={(value) => setSelectedManagers(value)}
                className="mt-md"
              />
            )}
          </div>
          <div className="flex items-center justify-end">
            <Button size="large" icon={<ReloadOutlined />} onClick={() => loadReport(TeamReportFormat.JSON)} />
          </div>
        </div>
        <Table
          dataSource={report}
          columns={columns}
          pagination={false}
          loading={loading}
          expandable={{
            expandedRowRender: renderDetails,
            rowExpandable: (item) => item.places_signed.length > 0 || item.places_enabled.length > 0,
          }}
          className="rounded-none shadow-card bg-white overflow-hidden"
          rowKey={(item) => item.manager.id}
        />
      </div>
    </>
  );
}
